/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { useCallback, useEffect, useMemo, useState } from "react"
import { FacetTypes, GrabXDataTypes, useGrabXFeatureFlag } from "grabx"
import { MerchantPortalFeatureFlag } from "grabx/constants"
import { generateDefaultFacets, withObserverModeFacet } from "grabx/facets"
import {
  MigrationChecklistMerchantStatus,
  MigrationChecklistResponse,
  useGetMigrationChecklistQuery,
  useGetMigrationChecklistStatusQuery,
} from "queries/garnetApi"
import { OutletStore } from "types/models/menu"

interface BuildMenuStoreMigrationMissingInfoAdapterResultArgs {
  stores: OutletStore[]
  isFeatureEnabled: boolean
  isMultiOutletMerchant: boolean
  isEligibleMerchant: boolean
  isStoreListLoading: boolean
  isStoreListError: boolean
  isStatusLoading: boolean
  isStatusError: boolean
  isStatusMappingIncomplete: boolean
  incompleteStoreIDs: string[]
}

interface BuildMenuStoreMigrationMissingInfoAdapterResult {
  isEnabled: boolean
  isLoading: boolean
  isError: boolean
  isEligibleMerchant: boolean
  hasAnyStoreMissingInfo: boolean
  shouldShowBanner: boolean
  shouldShowBlankMenuCta: boolean
  shouldShowErrorRetry: boolean
  missingInfoStoreIDs: string[]
}

export interface UseMenuStoreMigrationMissingInfoAdapterArgs {
  stores?: OutletStore[] | null
  isMultiOutletMerchant: boolean
  merchantGroupID?: string
  isStoreListLoading: boolean
  isStoreListError: boolean
}

export interface UseMenuStoreMigrationMissingInfoAdapterResult
  extends BuildMenuStoreMigrationMissingInfoAdapterResult {
  isStoreMissingInfo: (store: OutletStore) => boolean
  retryStatusFetch: () => void
}

export const MENU_MIGRATION_PREVIEW_MOCK_QUERY_KEY = "menuMigrationPreviewMock"
export const MENU_MIGRATION_PREVIEW_MOCK_STORE_ID = "preview-mock-store-id"

const EMPTY_RESULT: BuildMenuStoreMigrationMissingInfoAdapterResult = {
  isEnabled: false,
  isLoading: false,
  isError: false,
  isEligibleMerchant: false,
  hasAnyStoreMissingInfo: false,
  shouldShowBanner: false,
  shouldShowBlankMenuCta: false,
  shouldShowErrorRetry: false,
  missingInfoStoreIDs: [],
}

export const isMenuMigrationPreviewMockEnabled = (search?: string): boolean => {
  const isNonProductionEnvironment = process.env.NODE_ENV !== "production"

  if (!isNonProductionEnvironment) return false
  if (typeof window === "undefined" && !search) return false

  const locationSearch = search ?? window.location.search

  const searchParams = new URLSearchParams(locationSearch)
  return searchParams.get(MENU_MIGRATION_PREVIEW_MOCK_QUERY_KEY) === "1"
}

export const useMenuMigrationMissingInfoFeatureFlag = (
  merchantGroupID?: string,
) => {
  const facets = withObserverModeFacet({
    ...generateDefaultFacets(),
    ...(merchantGroupID && {
      [FacetTypes.MexMerchant]: merchantGroupID,
    }),
  })

  return useGrabXFeatureFlag<boolean>(
    MerchantPortalFeatureFlag.portalEnableMenuStoreMigrationMissingInfoWarning,
    GrabXDataTypes.Boolean,
    false,
    facets,
  )
}

export const getVisibleStoreMerchantIDs = (
  stores?: OutletStore[] | null,
): string[] => {
  return (stores || [])
    .map(store => store.merchantID)
    .filter((merchantID): merchantID is string => !!merchantID)
}

/**
 * Fail-all: every requested merchant ID must appear with a boolean
 * `is_completed`. Partial or malformed mappings must not drive warning UI.
 */
export const isMigrationChecklistStatusMappingComplete = (
  requestedMerchantIDs: string[],
  merchantStatuses: MigrationChecklistMerchantStatus[] | null | undefined,
): boolean => {
  if (!Array.isArray(merchantStatuses)) return false
  if (requestedMerchantIDs.length === 0) return true

  const statusByMerchantID = new Set(
    merchantStatuses
      .filter(
        (status): status is MigrationChecklistMerchantStatus =>
          !!status?.merchant_id && typeof status.is_completed === "boolean",
      )
      .map(status => status.merchant_id),
  )

  return requestedMerchantIDs.every(merchantID =>
    statusByMerchantID.has(merchantID),
  )
}

export const getIncompleteStoreIDsFromStatuses = (
  merchantStatuses: MigrationChecklistMerchantStatus[] | null | undefined,
  requestedMerchantIDs: string[],
): string[] => {
  if (!Array.isArray(merchantStatuses)) return []

  const requestedMerchantIDSet = new Set(requestedMerchantIDs)

  return merchantStatuses
    .filter(
      status =>
        status &&
        status.is_completed === false &&
        !!status.merchant_id &&
        requestedMerchantIDSet.has(status.merchant_id),
    )
    .map(status => status.merchant_id)
    .filter((merchantID): merchantID is string => !!merchantID)
}

/**
 * Eligibility from GET /v1/garnet/migration-checklist.
 * Prefer `incompleted_stores` (Andros). Temporary fallbacks until BE ships it.
 */
export const resolveIsEligibleMerchantFromChecklist = (
  response: MigrationChecklistResponse | null | undefined,
): boolean => {
  if (!response) return false

  // Contract field: presence of the array (including empty) = in cohort.
  if (Array.isArray(response.incompleted_stores)) {
    return true
  }

  // Temporary bridge until `incompleted_stores` is always returned.
  if (typeof response.should_show === "boolean") {
    return response.should_show
  }

  // Temporary mock: successful checklist payload without either field still
  // counts as eligible so this MR can merge before Andros' BE adjustment.
  // Remove once `incompleted_stores` is reliable on the contract.
  return true
}

export const buildMenuStoreMigrationMissingInfoAdapterResult = ({
  stores,
  isFeatureEnabled,
  isMultiOutletMerchant,
  isEligibleMerchant,
  isStoreListLoading,
  isStoreListError,
  isStatusLoading,
  isStatusError,
  isStatusMappingIncomplete,
  incompleteStoreIDs,
}: BuildMenuStoreMigrationMissingInfoAdapterResultArgs): BuildMenuStoreMigrationMissingInfoAdapterResult => {
  if (!isFeatureEnabled) {
    return {
      ...EMPTY_RESULT,
      isLoading: isStoreListLoading,
    }
  }

  // Store-list failure must not reuse cached checklist warnings. Retry is
  // checklist/status-API-only, so hide warning UI without showing that retry.
  if (isStoreListError) {
    return {
      ...EMPTY_RESULT,
      isEnabled: true,
      isLoading: false,
      isError: true,
      shouldShowErrorRetry: false,
    }
  }

  const isLoading = isStoreListLoading || isStatusLoading
  const isError = isStatusError || isStatusMappingIncomplete

  if (isLoading || !isMultiOutletMerchant || stores.length <= 0) {
    return {
      ...EMPTY_RESULT,
      isEnabled: true,
      isLoading,
      isError: false,
      shouldShowErrorRetry: false,
    }
  }

  if (isError) {
    return {
      ...EMPTY_RESULT,
      isEnabled: true,
      isLoading: false,
      isError: true,
      shouldShowErrorRetry: true,
    }
  }

  if (!isEligibleMerchant) {
    return {
      ...EMPTY_RESULT,
      isEnabled: true,
      isLoading: false,
      isError: false,
      isEligibleMerchant: false,
      shouldShowErrorRetry: false,
    }
  }

  const hasAnyStoreMissingInfo = incompleteStoreIDs.length > 0
  const shouldShowBanner = hasAnyStoreMissingInfo

  return {
    isEnabled: true,
    isLoading: false,
    isError: false,
    isEligibleMerchant: true,
    hasAnyStoreMissingInfo,
    shouldShowBanner,
    // Blank-menu CTA is not available from checklist-status; keep false.
    shouldShowBlankMenuCta: false,
    shouldShowErrorRetry: false,
    missingInfoStoreIDs: shouldShowBanner ? incompleteStoreIDs : [],
  }
}

const useMenuStoreMigrationMissingInfoAdapter = ({
  stores,
  isMultiOutletMerchant,
  merchantGroupID,
  isStoreListLoading,
  isStoreListError,
}: UseMenuStoreMigrationMissingInfoAdapterArgs): UseMenuStoreMigrationMissingInfoAdapterResult => {
  const isPreviewMockEnabled = isMenuMigrationPreviewMockEnabled()

  const {
    data: isRemoteFlagEnabled = false,
    isLoading: isRemoteFlagLoading,
    isError: isRemoteFlagError,
  } = useMenuMigrationMissingInfoFeatureFlag(merchantGroupID)

  const isFeatureEnabled = isRemoteFlagEnabled

  const visibleMerchantIDs = useMemo(() => getVisibleStoreMerchantIDs(stores), [
    stores,
  ])

  // Don't skip fetches while the store list is merely appending (Load more).
  const isInitialStoreListLoading =
    isStoreListLoading && (stores || []).length === 0

  const shouldFetchChecklist =
    !isPreviewMockEnabled &&
    isFeatureEnabled &&
    !isRemoteFlagLoading &&
    !isRemoteFlagError &&
    isMultiOutletMerchant &&
    !isInitialStoreListLoading &&
    !isStoreListError

  const {
    data: checklistResponse,
    isLoading: isChecklistQueryLoading,
    isFetching: isChecklistQueryFetching,
    isError: isChecklistQueryError,
    refetch: refetchChecklist,
  } = useGetMigrationChecklistQuery(undefined, {
    skip: !shouldFetchChecklist,
  })

  const isChecklistLoading =
    shouldFetchChecklist &&
    (isChecklistQueryLoading || isChecklistQueryFetching) &&
    !checklistResponse

  let isEligibleMerchant = false
  if (isPreviewMockEnabled) {
    isEligibleMerchant = true
  } else if (
    shouldFetchChecklist &&
    !isChecklistLoading &&
    !isChecklistQueryError
  ) {
    isEligibleMerchant = resolveIsEligibleMerchantFromChecklist(
      checklistResponse,
    )
  }
  // Incremental cache: Load more only fetches newly visible IDs and merges.
  // Entries not in the current visible set are dropped (search/filter reset).
  const [statusByMerchantID, setStatusByMerchantID] = useState<
    Record<string, MigrationChecklistMerchantStatus>
  >({})

  useEffect(() => {
    setStatusByMerchantID(previous => {
      const visibleSet = new Set(visibleMerchantIDs)
      let changed = false
      const next: Record<string, MigrationChecklistMerchantStatus> = {}

      Object.keys(previous).forEach(merchantID => {
        if (visibleSet.has(merchantID)) {
          next[merchantID] = previous[merchantID]
        } else {
          changed = true
        }
      })

      return changed ? next : previous
    })
  }, [visibleMerchantIDs])

  const unfetchedMerchantIDs = useMemo(
    () =>
      visibleMerchantIDs.filter(merchantID => !statusByMerchantID[merchantID]),
    [visibleMerchantIDs, statusByMerchantID],
  )

  const hasCachedVisibleStatuses = visibleMerchantIDs.some(
    merchantID => !!statusByMerchantID[merchantID],
  )

  const shouldFetchStatus =
    shouldFetchChecklist &&
    isEligibleMerchant &&
    !isChecklistLoading &&
    !isChecklistQueryError &&
    unfetchedMerchantIDs.length > 0

  const {
    data: statusResponse,
    isLoading: isStatusQueryLoading,
    isFetching: isStatusQueryFetching,
    isError: isStatusQueryError,
    refetch: refetchStatus,
  } = useGetMigrationChecklistStatusQuery(
    { merchantIDs: unfetchedMerchantIDs },
    { skip: !shouldFetchStatus },
  )

  useEffect(() => {
    const merchantStatuses = statusResponse?.merchant_statuses
    if (!Array.isArray(merchantStatuses)) {
      return
    }

    // Fail-all for the requested batch: do not merge partial pages (avoids a
    // refetch loop on the same unfetched IDs).
    if (
      !isMigrationChecklistStatusMappingComplete(
        unfetchedMerchantIDs,
        merchantStatuses,
      )
    ) {
      return
    }

    setStatusByMerchantID(previous => {
      const next = { ...previous }
      let changed = false

      merchantStatuses.forEach(status => {
        if (!status?.merchant_id) return
        next[status.merchant_id] = status
        changed = true
      })

      return changed ? next : previous
    })
  }, [statusResponse, unfetchedMerchantIDs])

  const knownVisibleMerchantIDs = useMemo(
    () =>
      visibleMerchantIDs.filter(merchantID => !!statusByMerchantID[merchantID]),
    [visibleMerchantIDs, statusByMerchantID],
  )

  const knownMerchantStatuses = useMemo(
    () =>
      knownVisibleMerchantIDs.map(merchantID => statusByMerchantID[merchantID]),
    [knownVisibleMerchantIDs, statusByMerchantID],
  )

  // Initial load only — keep prior warnings visible while Load more status runs.
  const isStatusLoading =
    isChecklistLoading ||
    (shouldFetchStatus &&
      !hasCachedVisibleStatuses &&
      (isStatusQueryLoading || isStatusQueryFetching))

  const isPendingBatchIncomplete =
    shouldFetchStatus &&
    !isStatusQueryLoading &&
    !isStatusQueryFetching &&
    !isStatusQueryError &&
    !!statusResponse &&
    !isMigrationChecklistStatusMappingComplete(
      unfetchedMerchantIDs,
      statusResponse.merchant_statuses,
    )

  const isStatusMappingIncomplete =
    isPendingBatchIncomplete ||
    (shouldFetchStatus === false &&
      isEligibleMerchant &&
      unfetchedMerchantIDs.length === 0 &&
      !isStatusQueryError &&
      !isStoreListError &&
      !isChecklistQueryError &&
      visibleMerchantIDs.length > 0 &&
      !isMigrationChecklistStatusMappingComplete(
        visibleMerchantIDs,
        knownMerchantStatuses,
      ))

  const incompleteStoreIDs = useMemo(
    () =>
      !isEligibleMerchant ||
      isStatusMappingIncomplete ||
      isStatusQueryError ||
      isChecklistQueryError ||
      isStoreListError
        ? []
        : getIncompleteStoreIDsFromStatuses(
            knownMerchantStatuses,
            knownVisibleMerchantIDs,
          ),
    [
      isEligibleMerchant,
      isStatusMappingIncomplete,
      isStatusQueryError,
      isChecklistQueryError,
      isStoreListError,
      knownMerchantStatuses,
      knownVisibleMerchantIDs,
    ],
  )

  const result = useMemo(() => {
    if (isPreviewMockEnabled) {
      const previewStoreIDs = [
        MENU_MIGRATION_PREVIEW_MOCK_STORE_ID,
        stores?.[0]?.merchantID,
      ].filter(Boolean) as string[]

      return {
        isEnabled: true,
        isLoading: false,
        isError: false,
        isEligibleMerchant: true,
        hasAnyStoreMissingInfo: true,
        shouldShowBanner: true,
        shouldShowBlankMenuCta: true,
        shouldShowErrorRetry: false,
        missingInfoStoreIDs: previewStoreIDs,
      }
    }

    return buildMenuStoreMigrationMissingInfoAdapterResult({
      stores: stores || [],
      isFeatureEnabled,
      isMultiOutletMerchant,
      isEligibleMerchant,
      // Load more keeps existing stores on screen — don't treat append fetch as
      // a full-list loading state that clears warnings.
      isStoreListLoading: isInitialStoreListLoading || isRemoteFlagLoading,
      isStoreListError,
      isStatusLoading,
      // Flag fetch errors keep the feature off; checklist/status errors retry.
      isStatusError: isStatusQueryError || isChecklistQueryError,
      isStatusMappingIncomplete,
      incompleteStoreIDs,
    })
  }, [
    stores,
    isPreviewMockEnabled,
    isFeatureEnabled,
    isMultiOutletMerchant,
    isEligibleMerchant,
    isInitialStoreListLoading,
    isStoreListError,
    isRemoteFlagLoading,
    isStatusLoading,
    isStatusQueryError,
    isChecklistQueryError,
    isStatusMappingIncomplete,
    incompleteStoreIDs,
  ])

  const missingInfoStoreIDsSet = useMemo(
    () => new Set(result.missingInfoStoreIDs),
    [result.missingInfoStoreIDs],
  )

  const isStoreMissingInfo = useCallback(
    (store: OutletStore) => missingInfoStoreIDsSet.has(store.merchantID),
    [missingInfoStoreIDsSet],
  )

  const retryStatusFetch = useCallback(() => {
    if (isChecklistQueryError) {
      refetchChecklist()
    }

    // First-failure / incomplete-mapping: nothing (or only a bad batch) may be
    // cached, so clearing alone does not change query args. Refetch the active
    // status subscription in those cases.
    const shouldRefetchStatus = isStatusQueryError || isPendingBatchIncomplete

    // Drop cached statuses for the current visible set so the next query
    // re-requests them (incremental query args must change to refetch).
    setStatusByMerchantID(previous => {
      if (visibleMerchantIDs.length === 0) return {}

      const visibleSet = new Set(visibleMerchantIDs)
      const next: Record<string, MigrationChecklistMerchantStatus> = {}
      let changed = false

      Object.keys(previous).forEach(merchantID => {
        if (visibleSet.has(merchantID)) {
          changed = true
          return
        }
        next[merchantID] = previous[merchantID]
      })

      return changed ? next : previous
    })

    if (shouldRefetchStatus) {
      refetchStatus()
    }
  }, [
    visibleMerchantIDs,
    isChecklistQueryError,
    refetchChecklist,
    isStatusQueryError,
    isPendingBatchIncomplete,
    refetchStatus,
  ])

  return {
    ...result,
    isStoreMissingInfo,
    retryStatusFetch,
  }
}

export default useMenuStoreMigrationMissingInfoAdapter
